import { useState } from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface Option {
  label: string
  value: string
}

interface MultiSelectProps {
  options: Option[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  maxItems?: number
  searchPlaceholder?: string
  className?: string
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select items...",
  maxItems = 5,
  searchPlaceholder = "Search...",
  className,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false)

  const handleUnselect = (item: string) => {
    onChange(selected.filter((i) => i !== item))
  }

  const handleSelect = (item: string) => {
    if (selected.includes(item)) {
      handleUnselect(item)
    } else {
      onChange([...selected, item])
    }
  }

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            <span className="truncate">
              {selected.length > 0 
                ? `${selected.length} selected`
                : placeholder
              }
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <div className="p-2">
              <CommandInput placeholder={searchPlaceholder} />
              <CommandEmpty>No options found.</CommandEmpty>
              <CommandGroup className="max-h-48 overflow-auto space-y-1">
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    onSelect={() => handleSelect(option.value)}
                    className="flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 cursor-pointer rounded"
                  >
                    <Check
                      className={cn(
                        "h-4 w-4",
                        selected.includes(option.value)
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    <span className="text-sm">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
              
              {/* Show selected values as badges inside dropdown */}
              {selected.length > 0 && (
                <div className="mt-2 pt-2 border-t">
                  <div className="flex flex-wrap gap-1 w-full overflow-hidden max-w-full">
                    {selected.map((value) => {
                      const option = options.find(opt => opt.value === value);
                      return (
                        <Badge key={value} variant="secondary" className="text-xs max-w-[120px] min-w-0 flex items-center">
                          <span className="truncate min-w-0 flex-1">{option?.label || value}</span>
                          <X 
                            className="w-3 h-3 ml-1 cursor-pointer flex-shrink-0" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnselect(value);
                            }}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}